"use client";

import { useEffect, useState, useCallback } from "react";
import { useParams, useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { logger } from "@/lib/logger";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BottomNav } from "@/components/bottom-nav";
import {
  ArrowLeft,
  CheckCircle2,
  MapPin,
  Calendar,
  DollarSign,
  Phone,
  Mail,
  MessageSquare,
  Loader2,
  AlertCircle,
} from "lucide-react";

interface TeamContact {
  id: string;
  name: string;
  role: string | null;
  phone: string | null;
  email: string | null;
  is_primary: boolean;
}

interface HiredJobDetails {
  matchId: string;
  hiredAt: string | null;
  job: {
    title: string;
    description: string;
    trade: string;
    suburb: string | null;
    siteAddress: string | null;
    rateMin: number | null;
    rateMax: number | null;
    rateType: string | null;
    startDate: string | null;
  };
  contractor: {
    profileId: string;
    name: string | null;
    phone: string | null;
    email: string | null;
    companyName: string | null;
  };
  teamContacts: TeamContact[];
}

export default function HiredJobDetailPage() {
  const params = useParams();
  const router = useRouter();
  const matchId = params.matchId as string;
  const supabase = createClient();
  const [details, setDetails] = useState<HiredJobDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startingChat, setStartingChat] = useState(false);

  const loadDetails = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { router.push("/auth/login"); return; }

      const { data: match, error: matchError } = await supabase
        .from("matches")
        .select(`
          id, status, hired_at, worker_id,
          jobs!inner(
            title, description, trade, suburb, site_address,
            rate_min, rate_max, rate_type, start_date,
            companies!inner(name, profile_id)
          )
        `)
        .eq("id", matchId)
        .eq("worker_id", user.id)
        .single();

      if (matchError || !match) {
        setError("Job not found or you don't have access.");
        setLoading(false);
        return;
      }

      const job = match.jobs as unknown as {
        title: string; description: string; trade: string;
        suburb: string | null; site_address: string | null;
        rate_min: number | null; rate_max: number | null; rate_type: string | null;
        start_date: string | null;
        companies: { name: string | null; profile_id: string };
      };

      // Fetch contractor profile and team contacts in parallel
      const companyProfileId = job.companies.profile_id;
      const [{ data: contractorProfile }, { data: companyData }] = await Promise.all([
        supabase.from("profiles").select("name, phone, email").eq("id", companyProfileId).single(),
        supabase.from("companies").select("id").eq("profile_id", companyProfileId).single(),
      ]);

      let teamContacts: TeamContact[] = [];
      if (companyData) {
        const { data: contacts } = await supabase
          .from("company_contacts")
          .select("*")
          .eq("company_id", companyData.id)
          .order("is_primary", { ascending: false })
          .order("name");
        teamContacts = contacts || [];
      }

      setDetails({
        matchId: match.id,
        hiredAt: match.hired_at,
        job: {
          title: job.title,
          description: job.description,
          trade: job.trade,
          suburb: job.suburb,
          siteAddress: job.site_address,
          rateMin: job.rate_min,
          rateMax: job.rate_max,
          rateType: job.rate_type,
          startDate: job.start_date,
        },
        contractor: {
          profileId: companyProfileId,
          name: contractorProfile?.name || null,
          phone: contractorProfile?.phone || null,
          email: contractorProfile?.email || null,
          companyName: job.companies.name,
        },
        teamContacts,
      });
      setLoading(false);
    } catch (err) {
      logger.error("Error loading hired job:", err);
      setError("Failed to load. Please try again.");
      setLoading(false);
    }
  }, [matchId, supabase, router]);

  useEffect(() => { loadDetails(); }, [loadDetails]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="mx-auto max-w-screen-sm px-4 py-6">
          <Button variant="ghost" onClick={() => router.back()} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          <div className="flex flex-col items-center py-12 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{error}</p>
            <Button onClick={() => { setLoading(true); setError(null); loadDetails(); }} className="mt-4">Try Again</Button>
          </div>
        </div>
        <BottomNav userType="worker" />
      </div>
    );
  }

  const contractorFirstName = details.contractor.name?.split(" ")[0] || "Contractor";
  const rate = details.job.rateMin && details.job.rateMax && details.job.rateMin !== details.job.rateMax
    ? `$${details.job.rateMin}–$${details.job.rateMax}`
    : details.job.rateMin ? `$${details.job.rateMin}` : details.job.rateMax ? `$${details.job.rateMax}` : null;

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="mx-auto max-w-screen-sm px-4 py-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="icon" onClick={() => router.back()}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Hired Job</h1>
        </div>

        {/* Hired banner */}
        <div className="rounded-2xl bg-green-500/10 border-2 border-green-500/30 p-5 mb-5 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-2" />
          <p className="font-bold text-green-800 text-2xl">You&apos;re Hired!</p>
          <p className="text-base text-green-700 mt-1">{details.job.title}</p>
          {details.contractor.companyName && (
            <p className="text-sm text-green-600 mt-0.5">at {details.contractor.companyName}</p>
          )}
          {details.hiredAt && (
            <p className="text-xs text-green-600 mt-2">
              {new Date(details.hiredAt).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          )}
        </div>

        {/* === STUPID SIMPLE CONTACT BUTTONS === */}
        <h3 className="font-bold text-lg mb-3">Contact {contractorFirstName}</h3>
        <div className="space-y-4 mb-6">

          {/* CALL — biggest, most prominent */}
          {details.contractor.phone && (
            <a href={`tel:${details.contractor.phone}`} className="block">
              <div className="flex items-center gap-4 rounded-2xl bg-green-600 p-5 text-white active:scale-[0.97] transition-transform shadow-lg">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20">
                  <Phone className="h-8 w-8" />
                </div>
                <div>
                  <p className="text-xl font-bold">Call {contractorFirstName}</p>
                  <p className="text-lg text-white/80">{details.contractor.phone}</p>
                </div>
              </div>
            </a>
          )}

          {/* SMS */}
          {details.contractor.phone && (
            <a href={`sms:${details.contractor.phone}`} className="block">
              <div className="flex items-center gap-4 rounded-2xl bg-blue-600 p-5 text-white active:scale-[0.97] transition-transform shadow-lg">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20">
                  <MessageSquare className="h-8 w-8" />
                </div>
                <div>
                  <p className="text-xl font-bold">Text {contractorFirstName}</p>
                  <p className="text-lg text-white/80">{details.contractor.phone}</p>
                </div>
              </div>
            </a>
          )}

          {/* EMAIL */}
          {details.contractor.email && (
            <a href={`mailto:${details.contractor.email}`} className="block">
              <div className="flex items-center gap-4 rounded-2xl bg-slate-700 p-5 text-white active:scale-[0.97] transition-transform shadow-lg">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20">
                  <Mail className="h-8 w-8" />
                </div>
                <div>
                  <p className="text-xl font-bold">Email {contractorFirstName}</p>
                  <p className="text-lg text-white/80 truncate">{details.contractor.email}</p>
                </div>
              </div>
            </a>
          )}

          {!details.contractor.phone && !details.contractor.email && (
            <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-5 text-center">
              <AlertCircle className="h-8 w-8 text-amber-600 mx-auto mb-2" />
              <p className="text-base font-medium text-amber-900">No contact details available yet</p>
              <p className="text-sm text-amber-700 mt-1">Send a message through the app below.</p>
            </div>
          )}

          <button
            onClick={async () => {
              if (!details?.contractor.profileId) return;
              setStartingChat(true);
              try {
                // Get CSRF token
                const csrfRes = await fetch("/api/auth/csrf");
                const { token: csrfToken } = await csrfRes.json();
                
                const res = await fetch("/api/messages/start", {
                  method: "POST",
                  headers: { "Content-Type": "application/json", "x-csrf-token": csrfToken },
                  body: JSON.stringify({ recipientId: details.contractor.profileId }),
                });
                if (res.ok) {
                  const data = await res.json();
                  router.push(`/messages/${data.conversationId}`);
                } else {
                  router.push("/messages");
                }
              } catch {
                router.push("/messages");
              } finally {
                setStartingChat(false);
              }
            }}
            disabled={startingChat}
            className="block w-full text-left"
          >
            <div className="flex items-center gap-4 rounded-2xl border-2 border-primary p-5 active:scale-[0.97] transition-transform">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                {startingChat ? (
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                ) : (
                  <MessageSquare className="h-8 w-8 text-primary" />
                )}
              </div>
              <div>
                <p className="text-xl font-bold">In-App Message</p>
                <p className="text-base text-muted-foreground">Chat within RateRight</p>
              </div>
            </div>
          </button>
        </div>

        {/* Team contacts */}
        {details.teamContacts.length > 0 && (
          <div className="mb-6">
            <h3 className="font-bold text-lg mb-3">Team Contacts</h3>
            <div className="space-y-3">
              {details.teamContacts.map((contact) => (
                <div key={contact.id} className="rounded-xl border p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="font-semibold text-base">{contact.name}</p>
                    {contact.role && (
                      <Badge variant="secondary" className="text-xs">{contact.role}</Badge>
                    )}
                    {contact.is_primary && (
                      <Badge className="bg-brand-orange/10 text-brand-orange text-xs border-0">Primary</Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {contact.phone && (
                      <a href={`tel:${contact.phone}`}>
                        <Button size="sm" variant="outline" className="h-10 text-sm gap-2">
                          <Phone className="h-4 w-4 text-green-600" />
                          {contact.phone}
                        </Button>
                      </a>
                    )}
                    {contact.email && (
                      <a href={`mailto:${contact.email}`}>
                        <Button size="sm" variant="outline" className="h-10 text-sm gap-2">
                          <Mail className="h-4 w-4 text-blue-600" />
                          {contact.email}
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Job details — collapsed, secondary to contact */}
        <details className="rounded-2xl border p-4 mb-4">
          <summary className="font-semibold text-base cursor-pointer">Job Details</summary>
          <div className="mt-3 space-y-2 text-sm text-muted-foreground">
            <Badge variant="secondary">{details.job.trade}</Badge>
            <p className="whitespace-pre-wrap mt-2">{details.job.description}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
              {details.job.suburb && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {details.job.suburb}</span>}
              {details.job.siteAddress && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {details.job.siteAddress}</span>}
              {rate && <span className="flex items-center gap-1 font-medium text-foreground"><DollarSign className="h-3.5 w-3.5" /> {rate}{details.job.rateType === "hourly" ? "/hr" : "/day"}</span>}
              {details.job.startDate && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Starts {new Date(details.job.startDate).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>}
            </div>
          </div>
        </details>

        <button
          onClick={() => router.push(`/report-issue?matchId=${details.matchId}&role=worker`)}
          className="w-full rounded-xl border border-border bg-card p-4 text-center transition-colors hover:bg-accent mt-4"
        >
          <div className="flex items-center justify-center gap-2 text-foreground">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Report an Issue</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Have a problem with this match? Let us know.
          </p>
        </button>
      </div>
      <BottomNav userType="worker" />
    </div>
  );
}
